/**
 * MessageSources — cited-document badges under a KI-Hofassistent agent
 * reply (#1757, subtask 07).
 *
 * The KI-Hofassistent attaches a structured ``sources`` array to each grounded
 * answer (subtask 06). Each source renders as a keyboard-accessible badge whose
 * visible text is the document number (Belegnummer); when the number is absent
 * it falls back to a short ``type · date`` label (never the opaque UUID).
 *
 * The region lives BENEATH the sanitized markdown body and is built from the
 * structured ``sources`` array — it is a plain React element, never markdown,
 * so ``rehype-sanitize`` stays untouched and model output can never become a
 * live link.
 *
 * Clicking a badge invokes ``onOpenDocument(document_id)``; the chat container
 * (``KiAssistantTab``) wires that to react-router navigation so the main app
 * deep-links to the document while the chat overlay stays mounted. The shared
 * ``MessageBubble`` never imports a router hook, so the admin messages tool
 * (which never carries ``sources``) stays inert.
 *
 * The type + date hover reuses the shadcn ``Tooltip`` primitive. The full
 * type + date + number is always on the badge's ``aria-label`` for screen
 * readers, independent of the hover.
 *
 * Tenant rule: every provided source is rendered as linkable. The component
 * performs NO document lookup — ``sources`` is already tenant-scoped
 * server-side (subtask 06 emits only the caller's own-tenant documents).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@hofsync/design-system';
import type { DocumentSource } from '../../services/chatApi';
import './MessageSources.css';

export interface MessageSourcesProps {
  sources: DocumentSource[];
  /** Wired by the chat container (KiAssistantTab) to react-router navigation.
   * Absent on the admin tool, where ``sources`` is never populated anyway. */
  onOpenDocument?: (documentId: string) => void;
}

/**
 * Format an ISO date (``YYYY-MM-DD`` or a full ISO timestamp) to German
 * ``DD.MM.YYYY``. The date part is parsed directly so a date-only string never
 * rolls back a day across a negative-offset timezone (mirrors DateSplitter's
 * DD.MM.YYYY output).
 */
function formatDocDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
}

function trimmed(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

export const MessageSources: React.FC<MessageSourcesProps> = ({
  sources,
  onOpenDocument,
}) => {
  const { t } = useTranslation();
  if (!sources.length) return null;

  return (
    <TooltipProvider>
      <div className="chat-sources" data-testid="chat-message-sources">
        {sources.map((source, idx) => {
          const number = trimmed(source.document_number);
          // #1930 B2a: the taxonomy-v2 role label wins; the stored
          // ``document_type`` snapshot stays as the fallback for old messages
          // (snapshots are forward-only — never rewritten).
          const type = trimmed(source.role_name) || trimmed(source.document_type);
          const date = source.document_date ? formatDocDate(source.document_date) : '';

          // ``type · date`` — the visible fallback when there is no Belegnummer,
          // and also the shadcn Tooltip hover text.
          const typeAndDate = [type, date].filter(Boolean).join(' · ');

          // Visible text: Belegnummer, else type · date, else a generic label —
          // never the opaque UUID.
          const label = number || typeAndDate || t('chat.sources.fallbackGeneric', 'Document');

          // Type + date hover (shadcn Tooltip).
          const hover = typeAndDate;

          // aria-label carries type + date + number (whatever is present).
          const details = [
            type,
            date,
            number ? t('chat.sources.numberAria', 'document number {{number}}', { number }) : '',
          ]
            .filter(Boolean)
            .join(', ');
          const ariaLabel = details
            ? t('chat.sources.openAria', 'Open document: {{detail}}', { detail: details })
            : t('chat.sources.open', 'Open document');

          const badge = (
            <button
              type="button"
              className="chat-source-badge"
              data-testid="chat-source-badge"
              data-document-id={source.document_id}
              aria-label={ariaLabel}
              onClick={() => onOpenDocument?.(source.document_id)}
            >
              <FileText className="chat-source-badge__icon" size={12} aria-hidden />
              <span className="chat-source-badge__label">{label}</span>
            </button>
          );

          return (
            <Tooltip key={`${source.document_id}-${idx}`}>
              <TooltipTrigger asChild>{badge}</TooltipTrigger>
              {hover ? <TooltipContent>{hover}</TooltipContent> : null}
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
};
